import Handlebars from 'handlebars';
import SimpleB2BUnit from '../../model/simple-b2b-unit';

interface EntryData {
  weight: number | null;
  description: string | null;
  entryValid: boolean;
}

interface ShippingOrderSubmitData {
  orderId: string;
  fileJwtToken: string;
}

export default class ShippingOrderDetailForm {

  private static readonly TOTAL_PRICE_ENDPOINT = `${window.ACC.config.encodedContextPath}/shippingOrders/price`;

  private readonly entriesContainer: JQuery = $('.js-entriesContainer');
  private readonly entriesTemplate = Handlebars.compile($('.js-entriesTemplate').html());

  private entriesData: EntryData[] = [];
  private readonly removeEntryButton: JQuery;
  private readonly deliveryCostInput: JQuery;
  private readonly billRecipientInput: JQuery;
  private readonly shipToPartyNameInput: JQuery;
  private readonly shipToPartyPostalCodeInput: JQuery;
  private readonly shipToPartyCityInput: JQuery;
  private readonly shipToPartyIdInput: JQuery;
  private readonly shipToPartyIdErrorContainer: JQuery;
  private readonly orderingPartyContactPersonInput: JQuery;
  private readonly shipToPartyContactPersonInput: JQuery;
  private readonly printShippingOrderButton: JQuery;
  private readonly acceptedTermsAndConditionsInput: JQuery;
  private readonly acceptedDataPrivacyPoliciesInput: JQuery;

  private readonly submitFormButton: JQuery;

  private readonly successMessage: string;
  private readonly errorMessage: string;

  private readonly MAX_NUMBER_OF_ENTRIES: number;

  private readonly deleteEntryButtonSelector: string = '.js-entryDeleteButton';
  private readonly deleteEntryModal: JQuery = $('.js-deleteEntryModal');

  private readonly deleteAllEntriesModalButton: JQuery = $('.js-deleteAllEntriesModalButton');
  private readonly deleteAllEntriesButtonSelector: string = '.js-entryDeleteAllEntriesButton';
  private readonly deleteAllEntriesModal: JQuery = $('.js-deleteAllEntriesModal');

  private readonly colorboxCloseButtonSelector: string = '.js-closeColorbox';

  constructor(private form: JQuery<HTMLFormElement>) {

    this.removeEntryButton = form.find('.js-removeEntryButton');
    this.deliveryCostInput = form.find('.js-deliveryCostInput');
    this.billRecipientInput = form.find('.js-billRecipientInput');
    this.shipToPartyNameInput = form.find('.js-shipToPartyNameInput');
    this.shipToPartyIdInput = form.find('.js-shipToPartyIdInput');
    this.shipToPartyIdErrorContainer = form.find('.js-shipToPartyIdErrorContainer');
    this.shipToPartyPostalCodeInput = form.find('.js-shipToPartyPostalCodeInput');
    this.shipToPartyCityInput = form.find('.js-shipToPartyCityInput');
    this.MAX_NUMBER_OF_ENTRIES = Number(form.data('max-entries'));

    this.orderingPartyContactPersonInput = form.find('.js-orderingPartyContactPersonInput');
    this.shipToPartyContactPersonInput = form.find('.js-shipToPartyContactPersonInput');
    this.acceptedTermsAndConditionsInput = form.find('.js-termsAndConditionsInput');
    this.acceptedDataPrivacyPoliciesInput = form.find('.js-dataPrivacyPoliciesInput');
    this.submitFormButton = form.find('.js-submitFormButton');
    this.printShippingOrderButton = form.find('.js-printShippingOrderButton');

    this.successMessage = form.data('submit-message') as string;
    this.errorMessage = form.data('price-error') as string;

    form.parsley();

    this.insertEmptyEntry();
    this.registerHandlebarsHelpers();
    this.bindShipToPartyInputChange();
    this.renderShippingOrderEntriesList(false);
    this.bindOpenDeleteModal();
    this.bindBillRecipientChange();
    this.bindFormValidations();
    this.bindFormSubmit();
    this.bindOpenDeleteAllEntriesModal();
  }

  isValid(): boolean {
    return this.form.parsley()
      .isValid()
      && this.getNumberOfValidEntries() >= 1
      && this.shipToPartyIdErrorContainer.html() == ''; // eslint-disable-line eqeqeq
  }

  disableSubmitButton(): void {
    this.submitFormButton.prop('disabled', true);
  }

  submitButtonControl(): void {
    this.submitFormButton.prop('disabled', !this.isValid());
  }

  bindFormValidations(): void {
    this.form.find(':required')
      .on('input', (e) => {
        $(e.target)
          .parsley()
          .validate();
      });

    this.form.parsley()
      .on('field:validated', () => {
        this.submitButtonControl();
      });
  }

  // eslint-disable-next-line class-methods-use-this
  registerHandlebarsHelpers(): void {
    Handlebars.registerHelper('isReadonly', (input) => (input === true ? 'readonly' : ''));
  }

  removeEntry(index: number): void {
    this.entriesData.splice(index, 1);
  }

  removeAllEntries(): void {
    this.entriesData = [];
    this.insertEmptyEntry();
  }

  renderShippingOrderEntriesList(checkPrice = true): void {
    this.entriesContainer.html(this.entriesTemplate(this.entriesData));
    this.bindInputsChange();
    this.submitButtonControl();
    this.deleteAllEntriesModalButton.prop('disabled', this.getNumberOfValidEntries() <= 0);
    if (checkPrice) this.calculateTotalPrice();
  }

  async calculateTotalPrice(): void {
    const deliveryCost = await this.getDeliveryCost(this.billRecipientInput.val() as string, this.getNumberOfValidEntries());
    this.deliveryCostInput.val(deliveryCost);
  }

  getNumberOfValidEntries(): number {
    let filledEntries = 0;
    this.entriesData.forEach((value) => {
      if (value.entryValid === true) filledEntries += 1;
    });
    return filledEntries;
  }

  bindInputsChange(): void {
    const submit = (e: JQuery.Event) => {
      const entryContainer = $(e.target)
        .closest('.js-shippingOrderEntry');
      const entryIndex = entryContainer.data('index') as number;
      const weightInput = entryContainer.find('.js-entryWeightInput');
      const descriptionInput = entryContainer.find('.js-entryDescriptionInput');

      weightInput.parsley()
        .validate();
      descriptionInput.parsley()
        .validate();

      const isValid = weightInput.parsley()
        .isValid() && descriptionInput.parsley()
        .isValid();

      if (isValid && weightInput.val() !== '' && descriptionInput.val() !== '') {
        this.entriesData[entryIndex] = {
          weight: parseInt(weightInput.val() as string, 10),
          description: descriptionInput.val() as string,
          entryValid: true,
        };

        if (this.getNumberOfValidEntries() < this.MAX_NUMBER_OF_ENTRIES) this.insertEmptyEntry();
        this.renderShippingOrderEntriesList();
        this.focusLastEntryDescription();
      }
    };

    this.entriesContainer
      .find('.js-shippingOrderEntry:last-of-type .js-entryWeightInput, .js-shippingOrderEntry:last-of-type .js-entryDescriptionInput')
      .on('blur', submit);

    this.entriesContainer
      .find('.js-shippingOrderEntry:last-of-type .js-entryWeightInput')
      .on('keyup', (e) => {
        if (e.keyCode === 13) submit(e);
      });
  }

  focusLastEntryDescription(): void {
    this.entriesContainer.find('.js-entryDescriptionInput')
      .last()
      .focus();
  }

  insertEmptyEntry(): void {
    this.entriesData.push({
      weight: null,
      description: null,
      entryValid: false,
    });
  }

  bindShipToPartyInputChange(): void {
    this.shipToPartyIdInput.on('blur keyup', (e) => {

      if (e.keyCode && e.keyCode !== 13) {
        return;
      }

      if ($(e.target)
        .val() === '') {
        this.shipToPartyIdErrorContainer.html('');
        return;
      }

      this.getB2Bunit($(e.target).val() as string)
        .then((data: SimpleB2BUnit) => {
          this.shipToPartyIdErrorContainer.html('');
          this.submitButtonControl();
          this.shipToPartyNameInput.val(data.name);
          this.shipToPartyPostalCodeInput.val(data.baseUnitAddress.postalCode);
          this.shipToPartyCityInput.val(data.baseUnitAddress.town);
        })
        .catch((err) => {
          this.shipToPartyIdErrorContainer.html(err.responseJSON);
          this.submitButtonControl();
          this.shipToPartyNameInput.val('');
          this.shipToPartyPostalCodeInput.val('');
          this.shipToPartyCityInput.val('');
        });
    });
  }

  bindBillRecipientChange(): void {
    this.billRecipientInput.on('change', () => {
      this.calculateTotalPrice();
    });
  }

  disableFormAfterSubmit(data: ShippingOrderSubmitData): void {
    this.deleteAllEntriesModalButton.remove();
    this.submitFormButton.remove();
    this.printShippingOrderButton.removeClass('d-none');
    this.printShippingOrderButton.attr('href', `/shippingOrders/print?token=${data.fileJwtToken}`);
    this.orderingPartyContactPersonInput.prop('readonly', true);
    this.shipToPartyIdInput.prop('readonly', true);
    this.shipToPartyContactPersonInput.prop('readonly', true);
    this.shipToPartyPostalCodeInput.prop('readonly', true);
    this.shipToPartyCityInput.prop('readonly', true);
    this.entriesContainer.find('.js-removeEntryButton')
      .remove();
    this.billRecipientInput.prop('disabled', true);
    this.acceptedTermsAndConditionsInput.prop('disabled', true);
    this.acceptedDataPrivacyPoliciesInput.prop('disabled', true);
    this.renderShippingOrderEntriesList(false);
  }

  // eslint-disable-next-line class-methods-use-this
  getB2Bunit(id: string): JQuery.jqXHR<SimpleB2BUnit> {
    return $.ajax({
      url: `${window.ACC.config.encodedContextPath}/b2bunit/${id}`,
      method: 'GET',
    }) as JQuery.jqXHR<SimpleB2BUnit>;
  }

  submitForm(): JQuery.jqXHR {
    const entries = this.entriesData;

    if (this.entriesData.length !== this.getNumberOfValidEntries()) entries.pop();

    return $.ajax({
      url: '/shippingOrders',
      headers: {
        CSRFToken: window.ACC.config.CSRFToken,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      data: JSON.stringify({
        orderingPartyContactPerson: this.orderingPartyContactPersonInput.val(),
        shipToPartyId: this.shipToPartyIdInput.val(),
        shipToPartyContactPerson: this.shipToPartyContactPersonInput.val(),
        billRecipient: this.billRecipientInput.val(),
        acceptedTermsAndConditions: this.acceptedTermsAndConditionsInput.val(),
        acceptedDataPrivacyPolicies: this.acceptedDataPrivacyPoliciesInput.val(),
        deliveryCost: this.deliveryCostInput.val(),
        entries,
      }),
    });
  }

  getDeliveryCost(billRecipient: string, numItems: number): Promise<string> {
    const jqPromise = $.get({
      url: ShippingOrderDetailForm.TOTAL_PRICE_ENDPOINT,
      data: {
        billRecipient,
        numItems,
      },
    })
      .fail((error) => {
        window.NotificationHelper.clearNotifications();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (error.responseJSON.length > 0) {
          window.NotificationHelper.showError(error.responseJSON);
        } else {
          window.NotificationHelper.showError(this.errorMessage);
        }
        return '';
      }) as JQuery.jqXHR<string>;
    return Promise.resolve(jqPromise);
  }

  bindFormSubmit(): void {
    this.submitFormButton.on('click', () => {

      this.disableSubmitButton();

      window.NotificationHelper.clearNotifications();

      this.submitForm()
        .then((data: ShippingOrderSubmitData) => {
          window.NotificationHelper.showInfo(this.successMessage.replace('{0}', data.orderId));
          this.disableFormAfterSubmit(data);
        })
        .catch((err) => {
          window.NotificationHelper.showError(err.responseJSON);
        });
    });
  }

  bindOpenDeleteModal(): void {
    this.entriesContainer.on('click', '.js-removeEntryButton', (e) => {

      const indexToRemove = $(e.target)
        .closest('.js-shippingOrderEntry')
        .data('index') as number;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      window.ACC.colorbox.open('', {
        html: this.deleteEntryModal.html(),
        width: '500px',
        onComplete: () => {
          this.bindDeleteEntry(indexToRemove);
          this.bindCloseColorboxButton();
        },
      });
    });
  }

  bindDeleteEntry(index: number): void {
    $(this.deleteEntryButtonSelector)
      .on('click', () => {
        this.removeEntry(index);
        $.colorbox.close();
        if (this.getNumberOfValidEntries() === this.entriesData.length) this.insertEmptyEntry();
        this.renderShippingOrderEntriesList();
      });
  }

  bindOpenDeleteAllEntriesModal(): void {
    this.deleteAllEntriesModalButton.on('click', () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      window.ACC.colorbox.open('', {
        html: this.deleteAllEntriesModal.html(),
        width: '500px',
        onComplete: () => {
          this.bindDeleteAllEntries();
          this.bindCloseColorboxButton();
        },
      });
    });
  }

  bindDeleteAllEntries(): void {
    $(this.deleteAllEntriesButtonSelector)
      .on('click', () => {
        this.removeAllEntries();
        $.colorbox.close();
        this.renderShippingOrderEntriesList();
      });
  }

  bindCloseColorboxButton(): void {
    $(this.colorboxCloseButtonSelector)
      .on('click', () => {
        $.colorbox.close();
      });
  }
}
